import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NotificationPreferencesForm } from "@/components/notification-preferences-form";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreferences,
} from "@/lib/api/user-notifications";
import { Alert, AlertContent, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { HStack } from "@/components/ui/h-stack";
import { Surface } from "@/components/ui/surface";
import { Skeleton } from "@/components/ui/skeleton";
import { SettingsPageLayout } from "@/components/ui/settings-page-layout";
import { VStack } from "@/components/ui/v-stack";
import { Icon } from "@/components/ui/icons";

function NotificationSettingsPage() {
  const navigate = useNavigate();
  const { t: tAccount } = useTranslation("account");
  const { t: tCommon } = useTranslation("common");
  const { t: tSettings } = useTranslation("settings");
  const queryClient = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: () => getNotificationPreferences(),
  });
  const preferences = prefsQuery.data ?? null;
  const isLoading = prefsQuery.isLoading;

  const [feedback, setFeedback] = useState<{
    error: "load" | "save" | null;
    success: "save" | null;
  }>({ error: prefsQuery.isError ? "load" : null, success: null });
  const errorKind = prefsQuery.isError ? "load" : feedback.error;
  const successKind = feedback.success;

  const handleSave = async (newPreferences: NotificationPreferences) => {
    try {
      setFeedback({ error: null, success: null });

      const data = await updateNotificationPreferences(newPreferences);

      queryClient.setQueryData(["notification-preferences"], data);
      setFeedback({ error: null, success: "save" });

      setTimeout(() => setFeedback((p) => ({ ...p, success: null })), 3000);
    } catch (err) {
      console.error("Error saving preferences:", err);
      setFeedback({ error: "save", success: null });
    }
  };

  return (
    <VStack gap="xl" data-testid="notification-settings-page">
      <PageNavigationHeader
        title={tSettings("notifications")}
        contextLabel={tSettings("account")}
        metadata={tAccount("notificationsDescription")}
        onBack={() => void navigate({ to: "/account" })}
        backAriaLabel={tCommon("back")}
        contextAriaLabel={tSettings("account")}
      />

      <SettingsPageLayout variant="list" className="space-y-6">
        {isLoading ? (
          <Card data-testid="notification-settings-loading">
            <VStack gap="lg">
              {Array.from({ length: 6 }).map((_, index) => (
                <Surface key={index} border="default" rounded="lg" padding="lg">
                  <HStack justify="between" gap="none">
                    <Skeleton className="h-5 w-44" />
                    <HStack align="stretch" gap="lg">
                      <Skeleton rounded="full" className="h-6 w-12" />
                      <Skeleton rounded="full" className="h-6 w-12" />
                      <Skeleton rounded="full" className="h-6 w-12" />
                    </HStack>
                  </HStack>
                </Surface>
              ))}
            </VStack>
          </Card>
        ) : null}

        {errorKind && (
          <Alert data-testid="error-message" color="danger">
            <AlertContent icon={<Icon.Alert size="md" />}>
              <AlertDescription>
                {errorKind === "load"
                  ? tCommon("errorLoadingData")
                  : tCommon("error")}
              </AlertDescription>
            </AlertContent>
          </Alert>
        )}

        {successKind && (
          <Alert data-testid="success-message" color="success">
            <AlertContent icon={<Icon.Check size="md" />}>
              <AlertDescription>
                {successKind === "save" ? tCommon("success") : null}
              </AlertDescription>
            </AlertContent>
          </Alert>
        )}

        {!isLoading && preferences ? (
          <Card variant="bordered">
            <NotificationPreferencesForm
              initialPreferences={preferences}
              onSave={handleSave}
              data-testid="preferences-form"
            />
          </Card>
        ) : null}
      </SettingsPageLayout>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/account/notifications")({
  component: NotificationSettingsPage,
});
